import { useEffect } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';

import { useAppContext } from '../context/AppContext';
import { useB2BMode } from '../hooks/useB2BMode';
import HomePage from '../pages/HomePage';
import IntroPageV2 from '../pages/IntroPageV2';
import WorkspacePage from '../pages/WorkspacePage';
import ProjectPage from '../pages/ProjectPage';
import StudyPage from '../pages/StudyPage';
import SettingsPage from '../pages/SettingsPage';
import Participants from '../pages/Participants';
import LoginPage from '../pages/LoginPage';
import IndividualLoginPage from '../pages/IndividualLoginPage';
import SuperLoginPage from '../pages/SuperLoginPage';
import AuthCallback from '../pages/AuthCallbackPage';
// Disabled for production: demo + legacy secret admin
// import DemoLoginPage from '../pages/DemoLoginPage';
import SessionDetailPage from '../pages/SessionDetailPage';
import NewStudyPage from '../pages/NewStudyPage';
import StudyEditPage from '../pages/StudyEditPage';
import StudyWorkflowPage from '../pages/StudyWorkflowPage';
import StudySchedulePage from '../pages/StudySchedulePage';
import TermsOfServicePage from '../pages/TermsOfServicePage';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
// import AdminDashboardPage from '../pages/AdminDashboardPage';
import ConversationStudyMakerPage from '../pages/ConversationStudyMakerPage';
import ChangePasswordPage from '../pages/ChangePasswordPage';
import SurveyDeployPage from '../pages/SurveyDeployPage';
import DevEvaluatorPage from '../pages/DevEvaluatorPage';
import InquiryPage from '../pages/InquiryPage';

import SuperAdminLayout from '../components/super-admin/SuperAdminLayout';
import SuperAdminDashboardPage from '../pages/super-admin/SuperAdminDashboardPage';
import EnterpriseListPage from '../pages/super-admin/EnterpriseListPage';
import GeneralAccountListPage from '../pages/super-admin/GeneralAccountListPage';
import CompanyListPage from '../pages/super-admin/CompanyListPage';
import CompanyDetailPage from '../pages/super-admin/CompanyDetailPage';
import CompanyUsagePage from '../pages/super-admin/CompanyUsagePage';
import InterviewCorpusPage from '../pages/super-admin/InterviewCorpusPage';
import DomainTemplatesPage from '../pages/super-admin/DomainTemplatesPage';
import AnalyticsPage from '../pages/super-admin/AnalyticsPage';

import B2BHomePage from '../pages/b2b/B2BHomePage';
import B2BSchedulesPage from '../pages/b2b/B2BSchedulesPage';
import B2BDashboardPage from '../pages/b2b/B2BDashboardPage';
import B2BSettingsPage from '../pages/b2b/B2BSettingsPage';
import B2BTeamPage from '../pages/b2b/B2BTeamPage';
import B2BSurveyDeployPage from '../pages/b2b/B2BSurveyDeployPage';
import B2BSurveyResponsesPage from '../pages/b2b/B2BSurveyResponsesPage';
import B2BAIInterviewPage from '../pages/b2b/B2BAIInterviewPage';
import PersonaListPage from '../features/persona/pages/PersonaListPage';
import ExplorePersonasPage from '../features/persona/pages/ExplorePersonasPage';
import PersonaFormPage from '../features/persona/pages/PersonaFormPage';
import PersonaDetailPage from '../features/persona/pages/PersonaDetailPage';
import TestsListPage from '../features/persona/pages/TestsListPage';
import TestCreatePage from '../features/persona/pages/TestCreatePage';
import UITestDetailPage from '../features/persona/pages/UITestDetailPage';
import UITestInsightPage from '../features/persona/pages/UITestInsightPage';
import InterviewInsightPage from '../features/persona/pages/InterviewInsightPage';
import ABTestDetailPage from '../features/persona/pages/ABTestDetailPage';
import FigmaSettingsPage from '../features/persona/pages/FigmaSettingsPage';

function withSuperAdminLayout(page) {
  return <SuperAdminLayout>{page}</SuperAdminLayout>;
}

function RouteLoadingFallback() {
  return (
    <div style={{ maxWidth: 480, margin: '80px auto', textAlign: 'center' }}>
      <p style={{ fontSize: '14px', color: '#6b7280' }}>로딩 중...</p>
    </div>
  );
}

function RequireB2CAccess({ children, allowEnterprise = false }) {
  const { isLoggedIn, sessionChecked, isEnterprise } = useAppContext();
  const { isB2BMode, enableB2BMode } = useB2BMode();

  useEffect(() => {
    if (!sessionChecked || !isLoggedIn || !isEnterprise || !allowEnterprise || isB2BMode) return;
    enableB2BMode();
  }, [allowEnterprise, enableB2BMode, isB2BMode, isEnterprise, isLoggedIn, sessionChecked]);

  if (!sessionChecked) return <RouteLoadingFallback />;
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  if (isEnterprise && !allowEnterprise) return <Navigate to="/b2b" replace />;
  if (isEnterprise && allowEnterprise && !isB2BMode) return <RouteLoadingFallback />;

  return children;
}

function withB2CAccess(page, options = {}) {
  return <RequireB2CAccess {...options}>{page}</RequireB2CAccess>;
}

function IndividualOnlyExplore({ children }) {
  const { isEnterprise, sessionChecked } = useAppContext();
  if (!sessionChecked) return <RouteLoadingFallback />;
  if (isEnterprise) return <Navigate to="/personas" replace />;
  return children;
}

function PersonasLandingRedirect() {
  const { isEnterprise, sessionChecked } = useAppContext();
  if (!sessionChecked) return <RouteLoadingFallback />;
  return <Navigate to={isEnterprise ? '/personas' : '/personas/explore'} replace />;
}

function GeneralRoutes() {
  return (
    <>
      <Route path="/" element={<IntroPageV2 />} />
      <Route path="/intro-legacy" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/individual/login" element={<IndividualLoginPage />} />
      <Route path="/super/login" element={<SuperLoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Retired demo / legacy secret-admin: no login UI, just leave the URL */}
      <Route path="/demo" element={<Navigate to="/" replace />} />
      <Route path="/demo/:secretPath" element={<Navigate to="/" replace />} />
      <Route path="/secret-admin-dashboard-x7y9z3" element={<Navigate to="/" replace />} />
      <Route path="/settings" element={withB2CAccess(<SettingsPage />)} />
      <Route path="/project/:projectSlug" element={withB2CAccess(<ProjectPage />, { allowEnterprise: true })} />
      <Route path="/study/new" element={withB2CAccess(<NewStudyPage />, { allowEnterprise: true })} />
      <Route path="/project/:projectSlug/study/new" element={withB2CAccess(<NewStudyPage />, { allowEnterprise: true })} />
      <Route path="/research/guideline/new" element={<Navigate to="/b2b" replace />} />
      <Route path="/research/survey/new" element={<Navigate to="/b2b" replace />} />
      <Route path="/project/:projectSlug/study/:studySlug/edit" element={withB2CAccess(<StudyEditPage />)} />
      <Route path="/study/:studySlug" element={withB2CAccess(<StudyWorkflowPage />, { allowEnterprise: true })} />
      <Route path="/study/:studySlug/schedule" element={withB2CAccess(<StudySchedulePage />)} />
      <Route path="/study/:studySlug/artifact/:artifactId" element={withB2CAccess(<StudyPage />, { allowEnterprise: true })} />
      <Route path="/study/:studySlug/survey/deploy" element={withB2CAccess(<SurveyDeployPage />)} />
      <Route path="/conversation-study-maker" element={withB2CAccess(<ConversationStudyMakerPage />, { allowEnterprise: true })} />
      <Route path="/project/:projectSlug/conversation-study-maker" element={withB2CAccess(<ConversationStudyMakerPage />, { allowEnterprise: true })} />
      <Route path="/dev/evaluator" element={withB2CAccess(<DevEvaluatorPage />)} />
      <Route path="/study/:studySlug/participants" element={withB2CAccess(<Participants />)} />
      <Route path="/scheduler/session/:sessionId" element={withB2CAccess(<SessionDetailPage />)} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/inquiry" element={<InquiryPage />} />
    </>
  );
}

function SuperAdminRoutes() {
  return (
    <>
      <Route path="/super-admin" element={withSuperAdminLayout(<SuperAdminDashboardPage />)} />
      <Route path="/super-admin/analytics" element={withSuperAdminLayout(<AnalyticsPage />)} />
      <Route path="/super-admin/enterprises" element={withSuperAdminLayout(<EnterpriseListPage />)} />
      <Route path="/super-admin/accounts" element={withSuperAdminLayout(<GeneralAccountListPage />)} />
      <Route path="/super-admin/companies" element={withSuperAdminLayout(<CompanyListPage />)} />
      <Route path="/super-admin/companies/:companyId/usage" element={withSuperAdminLayout(<CompanyUsagePage />)} />
      <Route path="/super-admin/companies/:companyId" element={withSuperAdminLayout(<CompanyDetailPage />)} />
      <Route path="/super-admin/corpus" element={withSuperAdminLayout(<InterviewCorpusPage />)} />
      <Route path="/super-admin/domain-templates" element={withSuperAdminLayout(<DomainTemplatesPage />)} />
    </>
  );
}

function B2BRoutes() {
  return (
    <>
      <Route path="/b2b" element={<B2BHomePage />} />
      <Route path="/b2b/home" element={<Navigate to="/b2b" replace />} />
      <Route path="/b2b/personas" element={<PersonasLandingRedirect />} />
      <Route path="/b2b/tests" element={<Navigate to="/tests" replace />} />
      <Route path="/research" element={withB2CAccess(<WorkspacePage />, { allowEnterprise: true })} />
      <Route path="/b2b/workspace" element={<Navigate to="/research" replace />} />
      <Route path="/b2b/schedules" element={<B2BSchedulesPage />} />
      <Route path="/b2b/dashboard" element={<B2BDashboardPage />} />
      <Route path="/b2b/settings" element={<B2BSettingsPage />} />
      <Route path="/b2b/settings/figma" element={<FigmaSettingsPage />} />
      <Route path="/b2b/team" element={<B2BTeamPage />} />
      <Route path="/b2b/study/:studySlug/survey-deploy" element={<B2BSurveyDeployPage />} />
      <Route path="/b2b/study/:studySlug/survey-responses" element={<B2BSurveyResponsesPage />} />
      <Route path="/b2b/study/:studySlug/ai-interview" element={<B2BAIInterviewPage />} />
    </>
  );
}

function PersonaRoutes() {
  return (
    <>
      <Route path="/personas" element={<PersonaListPage />} />
      <Route
        path="/personas/explore"
        element={(
          <IndividualOnlyExplore>
            <ExplorePersonasPage />
          </IndividualOnlyExplore>
        )}
      />
      <Route
        path="/personas/explore/:personaId"
        element={(
          <IndividualOnlyExplore>
            <PersonaDetailPage exploreCatalog />
          </IndividualOnlyExplore>
        )}
      />
      <Route path="/personas/new" element={<PersonaFormPage mode="create" />} />
      <Route path="/personas/:personaId" element={<PersonaDetailPage />} />
      <Route path="/personas/:personaId/edit" element={<PersonaDetailPage mode="edit" />} />
      <Route path="/tests" element={<TestsListPage />} />
      <Route path="/tests/new" element={<TestCreatePage />} />
      <Route path="/tests/interview/new" element={<B2BAIInterviewPage />} />
      <Route path="/tests/interview/:interviewId/insights" element={<InterviewInsightPage />} />
      <Route path="/tests/interview/:interviewId" element={<B2BAIInterviewPage />} />
      <Route path="/tests/interview/shareview/:shareToken" element={<B2BAIInterviewPage shareMode />} />
      <Route path="/tests/ui/shareview/:shareToken" element={<UITestDetailPage shareMode />} />
      <Route path="/tests/ui/:testId/insights" element={<UITestInsightPage />} />
      <Route path="/tests/ui/:testId" element={<UITestDetailPage />} />
      <Route path="/tests/ab/shareview/:shareToken" element={<ABTestDetailPage shareMode />} />
      <Route path="/tests/ab/:abTestId" element={<ABTestDetailPage />} />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {GeneralRoutes()}
      {SuperAdminRoutes()}
      {B2BRoutes()}
      {PersonaRoutes()}
    </Routes>
  );
}

export default AppRoutes;
